import { useMemo } from "react";
import { useValuesStore } from "@client/store/values";
import { useTicker } from "./useTicker";

export interface UseTopicsResult {
  values: Record<string, MQTTValue["value"]>;
  topics: Record<string, MQTTValue["topic"]>;
  timestamps: Record<string, Date>;
  suspicious: Record<string, boolean>;
  lastUpdated: Record<string, number>;
  lastValueIntervals: Record<string, number | undefined>;
  animationDurations: Record<string, number>;
}

const HARD_LIMIT_MS = 2 * 60 * 1000;

/**
 * Multi-topic verze useTopic.
 *
 * Klíčový perf rozdíl oproti původní implementaci:
 *   - Žádné `setInterval` per topic (původně N timerů pro N topiců)
 *   - Žádný `stickyDerived` state - data přichází přímo z Zustand store
 *   - Single `useTicker` re-render každých 500ms (sdíleno celou app)
 */
export const useTopics = (topicIds: string[]): UseTopicsResult => {
  const byTopic = useValuesStore((s) => s.byTopic);
  const tick = useTicker();

  // Stabilní key pro topicIds (změna pole vyvolá re-memo)
  const topicsKey = topicIds.join("|");

  return useMemo(() => {
    const values: Record<string, MQTTValue["value"]> = {};
    const topics: Record<string, MQTTValue["topic"]> = {};
    const timestamps: Record<string, Date> = {};
    const suspicious: Record<string, boolean> = {};
    const lastUpdated: Record<string, number> = {};
    const lastValueIntervals: Record<string, number | undefined> = {};
    const animationDurations: Record<string, number> = {};

    // Date.now() je tu OK - memo se přepočítá při změně tick (každých 500ms)
    // eslint-disable-next-line react-hooks/purity
    const now = Date.now();

    for (const id of topicIds) {
      const val = byTopic.get(id);
      if (!val) continue;

      values[id] = val.value;
      topics[id] = val.topic;
      timestamps[id] = val.timestamp;

      const ts = val.timestamp.getTime();
      const sinceLast = now - ts;

      const interval = val.prev ? ts - val.prev.timestamp.getTime() : undefined;
      lastValueIntervals[id] = interval;

      const allowedAge = interval ? interval * 2 : HARD_LIMIT_MS;
      suspicious[id] = sinceLast > allowedAge;
      lastUpdated[id] = Math.round(sinceLast / 1000);

      if (interval) {
        animationDurations[id] = Math.max(0, ts + interval - now);
      }
    }

    return { values, topics, timestamps, suspicious, lastUpdated, lastValueIntervals, animationDurations };
    // topicsKey je derivovaný z topicIds (join na začátku); topicIds samotné nelze v deps (pole instance se mění každý render)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [byTopic, tick, topicsKey]);
};
